/**
 * One FCC party (licensee, licensee contact, owner) rendered as a card.
 *
 * Shared by the license detail page and the CRM client page: the same party has
 * to read the same way in both places, and the name/address assembly rules (an
 * organisation name or a person's four name parts; ATTN / PO Box / street / city
 * state zip) are the kind of thing that drifts when it is copied.
 */
import type { FCCEntity } from '../types'
import { formatPhone, getEntityAddress, getEntityDisplayName } from '../utils/entityDisplay'

export default function EntityCard({
  entity,
  isPrimary,
  callSigns,
}: {
  entity: FCCEntity
  isPrimary: boolean
  /** Which call signs this party appears on — the client page lists several licenses. */
  callSigns?: string[]
}) {
  const addressLines = getEntityAddress(entity)
  const label = entity.entity_type_label || entity.entity_type || 'Entity'

  return (
    <div className={`border rounded-lg p-4 ${isPrimary ? 'border-gray-300' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${isPrimary ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'}`}>
          {label}
        </span>
        {entity.frn && (
          <span className="text-xs text-gray-500 font-mono">FRN: {entity.frn}</span>
        )}
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        <div>
          <dt className="text-xs font-medium text-gray-500">Name</dt>
          <dd className="mt-0.5 text-sm text-gray-900">{getEntityDisplayName(entity)}</dd>
        </div>

        {entity.phone && (
          <div>
            <dt className="text-xs font-medium text-gray-500">Phone</dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              <a href={`tel:${entity.phone}`} className="tap text-primary-600 hover:text-primary-800">
                {formatPhone(entity.phone)}
              </a>
            </dd>
          </div>
        )}

        {entity.fax && (
          <div>
            <dt className="text-xs font-medium text-gray-500">Fax</dt>
            <dd className="mt-0.5 text-sm text-gray-900">{formatPhone(entity.fax)}</dd>
          </div>
        )}

        {entity.email && (
          <div>
            <dt className="text-xs font-medium text-gray-500">Email</dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              <a href={`mailto:${entity.email}`} className="tap text-primary-600 hover:text-primary-800">
                {entity.email}
              </a>
            </dd>
          </div>
        )}

        {addressLines.length > 0 && (
          <div className="sm:col-span-2">
            <dt className="text-xs font-medium text-gray-500">Address</dt>
            <dd className="mt-0.5 text-sm text-gray-900">
              {addressLines.map((line, i) => (
                <span key={i}>{line}{i < addressLines.length - 1 && <br />}</span>
              ))}
            </dd>
          </div>
        )}

        {callSigns && callSigns.length > 0 && (
          <div className="sm:col-span-2">
            <dt className="text-xs font-medium text-gray-500">On licenses</dt>
            <dd className="mt-0.5 text-sm text-gray-900 font-mono">{callSigns.join(' · ')}</dd>
          </div>
        )}
      </dl>
    </div>
  )
}
